import { memo } from 'react';
import { Button, Modal, ModalBody, ModalContent } from '@heroui/react';
import { CHAT_MODAL_WIDTH_CLASS } from '../../config/constants';
import type { ChatMessage, ChatReportReason } from '../../model/types';
import { IconChevronDown, IconClose } from '../icons';

type ReportReasonOption = {
  value: ChatReportReason;
  label: string;
};

const REPORT_REASON_OPTIONS: ReportReasonOption[] = [
  { value: 'spam', label: 'Spam' },
  { value: 'harassment', label: 'Harassment' },
  { value: 'hate_speech', label: 'Hate speech' },
  { value: 'scam', label: 'Scam' },
  { value: 'inappropriate_content', label: 'Inappropriate content' },
  { value: 'violence_or_threat', label: 'Violence or threat' },
  { value: 'impersonation', label: 'Impersonation' },
  { value: 'other', label: 'Other' }
];

export type ReportMessageModalProps = {
  isOpen: boolean;
  message: ChatMessage | null;
  selectedReason: ChatReportReason | '';
  comment: string;
  isSubmitting: boolean;
  errorMessage: string | null;
  onReasonChange: (value: ChatReportReason | '') => void;
  onCommentChange: (value: string) => void;
  onClose: () => void;
  onSubmit: () => void | Promise<void>;
};

function getMessagePreview(message: ChatMessage | null) {
  if (!message) return '';
  if (message.text?.trim()) return message.text.trim();
  if (message.attachments.length) return `Attachment: ${message.attachments[0].name}`;
  return 'Message';
}

export const ReportMessageModal = memo(
  ({
    isOpen,
    message,
    selectedReason,
    comment,
    isSubmitting,
    errorMessage,
    onReasonChange,
    onCommentChange,
    onClose,
    onSubmit,
  }: ReportMessageModalProps) => (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      placement="center"
      hideCloseButton
      classNames={{
          base: `${CHAT_MODAL_WIDTH_CLASS} rounded-[28px] bg-white shadow-[0_24px_48px_rgba(30,30,30,0.12)]`,
          body: 'p-0',
          wrapper: 'px-2',
          backdrop: 'bg-black/30',
        }}
      >
      <ModalContent>
        <ModalBody>
          <div className="flex flex-col gap-6 px-6 py-7 sm:px-8">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h2 className="text-[30px] font-semibold leading-[110%] tracking-[-0.02em] text-[#1E1E1E]">
                  Report
                </h2>
                <p className="mt-6 text-sm leading-5 text-[#1E1E1E]">
                  Please select the reason for reporting this content. Our moderators will review it shortly
                </p>
              </div>
              <button
                type="button"
                className="mt-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[#1E1E1E] transition-colors hover:bg-black/[0.04]"
                onClick={onClose}
                aria-label="Close report modal"
                >
                <IconClose className="h-[18px] w-[18px]" />
              </button>
            </div>

            {message ? (
              <div className="rounded-[8px] bg-[#F5F5F5] px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-[0.06em] text-[#7A7A7A]">
                  Reported message
                </p>
                <p className="mt-2 line-clamp-3 whitespace-pre-wrap break-words text-sm leading-5 text-[#1E1E1E]">
                  {getMessagePreview(message)}
                </p>
              </div>
              ) : null}

            <div className="flex flex-col gap-5">
              <label className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-[#1E1E1E]">Select complaint</span>
                <div className="relative">
                  <select
                    value={selectedReason}
                    onChange={(event) =>
                        onReasonChange((event.target.value as ChatReportReason | '') || '')
                      }
                    className="h-14 w-full appearance-none rounded-[8px] border border-[#D3D3D3] bg-white px-4 pr-12 text-base text-[#1E1E1E] outline-none transition-colors focus:border-[#1E1E1E]"
                    >
                    <option value="">Choose a reason</option>
                    {REPORT_REASON_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                      ))}
                  </select>
                  <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-[#7A7A7A]">
                    <IconChevronDown className="h-[18px] w-[18px]" />
                  </span>
                </div>
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-[#1E1E1E]">Comment</span>
                <textarea
                  value={comment}
                  onChange={(event) => onCommentChange(event.target.value)}
                  rows={5}
                  placeholder="Comment"
                  className="min-h-[144px] w-full resize-none rounded-[8px] border border-[#D3D3D3] bg-white px-4 py-4 text-base leading-6 text-[#1E1E1E] outline-none transition-colors placeholder:text-[#A0A0A0] focus:border-[#1E1E1E]"
                  />
              </label>
            </div>

            {errorMessage ? (
              <p className="rounded-[8px] bg-[#FFF1F1] px-4 py-3 text-sm font-medium text-[#D13939]">
                {errorMessage}
              </p>
              ) : null}

            <div className="flex items-center justify-end gap-3">
              <Button
                variant="bordered"
                radius="full"
                className="w-full h-12 bg-black/[0.04] px-6 text-sm font-semibold text-[#1E1E1E]"
                onPress={onClose}
                isDisabled={isSubmitting}
                >
                Cancel
              </Button>
              <Button
                radius="full"
                className="w-full h-12 bg-[#211D55] px-6 text-sm font-semibold text-white"
                isLoading={isSubmitting}
                isDisabled={!selectedReason}
                onPress={onSubmit}
                >
                Submit
              </Button>
            </div>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
    ),
);

ReportMessageModal.displayName = 'ReportMessageModal';
